// MUI theme for the Restaurant Profile page (VendorProfile.jsx) ONLY. The page
// passes it to a nested ThemeProvider, so it extends the app theme for every
// control on the page, including the Select menus, which are portaled in the
// DOM but still sit inside the React tree. Nothing outside the page sees it.
import { createTheme } from '@mui/material/styles'
import { JAKARTA, typographyWithFont } from '../utils/plusJakartaTheme'

export const PF = {
  font: JAKARTA,
  text: '#0F172A',
  label: '#1E293B',
  secondary: '#475467',
  page: '#F4F5F7',
  card: '#FFFFFF',
  border: '#E1E5EA',
  inputBorder: '#8793A5',
  placeholder: '#667085',
  primary: '#15803D',
  primaryHover: '#166534',
  primaryDisabled: '#7DB592',
  indigo: '#4338CA',
  indigoText: '#3730A3',
  indigoSoft: '#EEF0FF',
  onBorder: '#A7D7B8',
  onBg: '#F1FAF4',
  offBorder: '#D0D5DD'
}

export const focusRing = { outline: `2px solid ${PF.indigo}`, outlineOffset: '2px' }

const components = {
  MuiTypography: {
    styleOverrides: {
      root: { color: PF.text },
      colorTextSecondary: { color: PF.secondary }
    }
  },
  MuiPaper: {
    styleOverrides: { root: { color: PF.text } }
  },
  MuiFormControlLabel: {
    styleOverrides: { label: { color: PF.text } }
  },
  MuiListItemText: {
    styleOverrides: {
      primary: { color: PF.text },
      secondary: { color: PF.secondary }
    }
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 8, fontSize: 14, alignItems: 'center' },
      message: { color: 'inherit' }
    }
  },

  // ---- Fields: inputs and selects share one look ----
  MuiInputBase: {
    styleOverrides: { input: { color: PF.text } }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        backgroundColor: PF.card,
        fontSize: 15,
        color: PF.text,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: PF.inputBorder },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PF.label },
        '&.Mui-focused': focusRing,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PF.indigo, borderWidth: 1 },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#B42318' }
      },
      input: {
        color: PF.text,
        height: 44,
        boxSizing: 'border-box',
        padding: '0 14px',
        '&::placeholder': { color: PF.placeholder, opacity: 1 }
      },
      // The show/hide button sits flush inside the right edge of the field.
      adornedEnd: { paddingRight: 0 },
      inputAdornedEnd: { paddingRight: 0 }
    }
  },
  MuiSelect: {
    styleOverrides: {
      select: {
        color: PF.text,
        '&.MuiSelect-select': {
          minHeight: 44,
          height: 'auto',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box'
        },
        '&&&': { paddingRight: 44 }
      },
      icon: { right: 12, color: PF.secondary, fontSize: 22 }
    }
  },
  MuiInputAdornment: {
    styleOverrides: { positionEnd: { marginLeft: 0 } }
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: PF.secondary,
        '&.Mui-focusVisible': focusRing,
        '.MuiInputAdornment-positionEnd > &': {
          width: 44,
          height: 44,
          marginRight: 0,
          borderRadius: '0 8px 8px 0'
        }
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        color: PF.text,
        fontSize: 15,
        minHeight: 44,
        '&.Mui-selected': { backgroundColor: PF.indigoSoft },
        '&.Mui-selected:hover': { backgroundColor: '#E2E5FF' },
        '&.Mui-focusVisible': { backgroundColor: '#E2E5FF' }
      }
    }
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: PF.inputBorder,
        '&.Mui-checked': { color: PF.indigo }
      }
    }
  },

  // ---- Buttons ----
  MuiButton: {
    styleOverrides: {
      root: {
        height: 44,
        minHeight: 44,
        borderRadius: 8,
        padding: '0 22px',
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: 0,
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
        '&.Mui-focusVisible': focusRing
      },
      contained: {
        backgroundColor: PF.primary,
        color: '#FFFFFF',
        '&:hover': { backgroundColor: PF.primaryHover },
        '&:active': { boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: PF.primaryDisabled, color: '#FFFFFF' }
      },
      outlined: {
        backgroundColor: '#FFFFFF',
        border: `1px solid ${PF.indigo}`,
        color: PF.indigoText,
        '&:hover': { backgroundColor: PF.indigoSoft, borderColor: PF.indigo },
        '&.Mui-disabled': { borderColor: PF.offBorder, color: PF.placeholder }
      }
    }
  },

  // ---- Switch: 40x24 track, 18px knob ----
  // Everything sits under root with descendant selectors so it beats the
  // size="small" and color="warning|success" styles the switches still carry.
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 40,
        height: 24,
        padding: 0,
        overflow: 'visible',
        flexShrink: 0,
        '& .MuiSwitch-switchBase': {
          padding: 3,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: 'transparent' }
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
          transform: 'translateX(16px)',
          color: '#FFFFFF',
          '&:hover': { backgroundColor: 'transparent' }
        },
        '& .MuiSwitch-thumb': {
          width: 18,
          height: 18,
          boxShadow: '0 1px 2px rgba(16, 24, 40, 0.24)'
        },
        '& .MuiSwitch-track': {
          borderRadius: 12,
          backgroundColor: PF.inputBorder,
          opacity: 1,
          transition: 'background-color 150ms ease'
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: PF.primary,
          opacity: 1
        },
        '& .MuiSwitch-switchBase.Mui-focusVisible + .MuiSwitch-track': focusRing,
        '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 }
      }
    }
  }
}

export const profileTheme = outer => {
  const typography = typographyWithFont(outer, PF.font)
  typography.body1 = { ...typography.body1, fontSize: '0.9375rem', lineHeight: 1.45 }
  typography.body2 = { ...typography.body2, fontSize: '0.875rem', lineHeight: 1.45 }
  typography.button = { ...typography.button, textTransform: 'none', fontWeight: 600, letterSpacing: 0 }
  return createTheme({
    ...outer,
    palette: {
      ...outer.palette,
      text: { ...outer.palette.text, primary: PF.text, secondary: PF.secondary }
    },
    typography,
    components: { ...outer.components, ...components }
  })
}
